import { DeepgramTranscribeSettings, OptionsApi } from '@core/swagger-client';
import { Beta, CompositePageHeader, ContainerNoTopSpace, ErrorMessagePage, Heading, Input, LoaderPage, PageHeader, PageHeaderContent, Select } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const LiveTranscribePage = () => {
    const { webinarId } = useParams({ strict: false });

    const { t } = useTranslation();
    const { data: webinar, isLoading: isWebinarLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const [deepgramTrackId, setDeepgramTrackId] = useState<string | null>(null);
    const {
        data: deepgramSettings,
        isLoading: isDeepgramSettingsLoading,
        error: deepgramSettingsError,
        refetch: refetchDeepgramSettings
    } = useQuery(OptionsApi.liveTranscribe.getDeepgramSettings(webinarId!));
    const [deepgramEnglishOnly, setDeepgramEnglishOnly] = useState(false);

    useEffect(() => {
        if (deepgramSettings) {
            setDeepgramEnglishOnly(deepgramSettings.model === 'flux-general-en' || deepgramSettings.model === 'nova-3-medical');
            setDeepgramTrackId(deepgramSettings.trackId);
        }
    }, [deepgramSettings]);

    useEffect(() => {
        if (!deepgramTrackId) return;

        if (deepgramSettings && deepgramTrackId !== deepgramSettings.trackId) {
            updateDeepgramSettings({ ...deepgramSettings, trackId: deepgramTrackId });
        }
    }, [deepgramTrackId, deepgramSettings]);

    const setDeepgramSettingsMutatation = useMutation(OptionsApi.liveTranscribe.setDeepgramSettings());
    const updateDeepgramSettings = (settings: DeepgramTranscribeSettings) => {
        // Ensure language matches model constraints
        if (settings.model === 'flux-general-en' || settings.model === 'nova-3-medical') {
            settings.language = 'en';
        }

        setDeepgramSettingsMutatation.mutate(
            { webinarId: webinarId!, settings },
            {
                onSuccess: () => {
                    refetchDeepgramSettings();
                }
            }
        );
    };

    const error = webinarError || deepgramSettingsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isWebinarLoading || !webinar || isDeepgramSettingsLoading || !deepgramSettings) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <div>
                <PageHeader>
                    <PageHeaderContent>
                        {webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}
                    </PageHeaderContent>
                </PageHeader>

                <div className="px:4 md:px-6 pt-4 md:pt-10 max-w-4xl">
                    <BackToOriginLink fallback="content" />
                    <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                        {t('LiveTranscribe.PageHeading')}
                    </Heading>
                    <div className="bg-white bg-side-warning bg-no-repeat rounded-lg flex flex-col items-start gap-1 mb-4 pl-6 md:pl-8 pr-4 md:pr-6 pt-4 pb-6 z-10">
                        <Beta classNames="w-10 fill-warning mb-2" />
                        <Heading variant="h3">{t('Beta.Heading')}</Heading>
                        <p className="max-w-3xl mb-4">{t('Beta.Text.LiveTranscribe')}</p>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-4 md:p-8">
                        <Heading variant="h2" classNames="mb-4 lg:mb-6">
                            {t('LiveTranscribe.SectionHeader.DeepgramSettings')}
                        </Heading>
                        <div className="pl-10">
                            <input
                                id="deepgram.enabled"
                                type="checkbox"
                                role="switch"
                                checked={deepgramSettings.isEnabled}
                                onChange={(e) => updateDeepgramSettings({ ...deepgramSettings, isEnabled: e.target.checked })}
                                className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                disabled={setDeepgramSettingsMutatation.isPending}
                            />
                            <label htmlFor="deepgram.enabled" className="inline-block">
                                {t('LiveTranscribe.Label.DeepgramEnabled')}
                            </label>
                        </div>
                        <div className="pl-10 mb-6">
                            <input
                                id="deepgram.default"
                                type="checkbox"
                                role="switch"
                                checked={deepgramSettings.isDefault}
                                onChange={(e) => updateDeepgramSettings({ ...deepgramSettings, isDefault: e.target.checked })}
                                className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                disabled={setDeepgramSettingsMutatation.isPending}
                            />
                            <label htmlFor="deepgram.default" className="inline-block">
                                {t('LiveTranscribe.Label.DeepgramDefault')}
                            </label>
                        </div>
                        <Input
                            classNames="mb-6"
                            label={t('LiveTranscribe.Label.TrackId')}
                            value={deepgramTrackId || ''}
                            onChange={(e) => setDeepgramTrackId(e.target.value)}
                        />
                        <Select
                            name="model"
                            classNames="mb-6"
                            label={t('LiveTranscribe.Label.DeepgramModel')}
                            value={deepgramSettings.model}
                            onChange={(e) => updateDeepgramSettings({ ...deepgramSettings, model: e.target.value })}
                            disabled={setDeepgramSettingsMutatation.isPending}
                        >
                            <option key="nova-3" value="nova-3">
                                nova-3
                            </option>
                            <option key="nova-3-general" value="nova-3-general">
                                nova-3-general
                            </option>
                            <option key="nova-3-medical" value="nova-3-medical">
                                nova-3-medical
                            </option>
                            <option key="flux-general-en" value="flux-general-en">
                                flux-general-en
                            </option>
                        </Select>
                        <Select
                            name="model"
                            classNames="mb-6"
                            label={t('LiveTranscribe.Label.DeepgramLanguage')}
                            value={deepgramSettings.language}
                            onChange={(e) => updateDeepgramSettings({ ...deepgramSettings, language: e.target.value })}
                            disabled={setDeepgramSettingsMutatation.isPending}
                        >
                            {!deepgramEnglishOnly && (
                                <option key="multi" value="multi">
                                    {t('LiveTranscribe.Option.DeepgramLanguage.Multi')}
                                </option>
                            )}
                            <option key="en" value="en">
                                {t('LiveTranscribe.Option.DeepgramLanguage.English')}
                            </option>
                            {!deepgramEnglishOnly && (
                                <>
                                    <option key="nl" value="nl">
                                        {t('LiveTranscribe.Option.DeepgramLanguage.Dutch')}
                                    </option>
                                    <option key="fr" value="fr">
                                        {t('LiveTranscribe.Option.DeepgramLanguage.French')}
                                    </option>
                                    <option key="de" value="de">
                                        {t('LiveTranscribe.Option.DeepgramLanguage.German')}
                                    </option>
                                    {/* More language options can be added here: https://developers.deepgram.com/docs/models-languages-overview */}
                                </>
                            )}
                        </Select>
                        <div className="pl-10">
                            <input
                                id="deepgram.smartFormat"
                                type="checkbox"
                                role="switch"
                                checked={deepgramSettings.smartFormat}
                                onChange={(e) => updateDeepgramSettings({ ...deepgramSettings, smartFormat: e.target.checked })}
                                className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                disabled={setDeepgramSettingsMutatation.isPending}
                            />
                            <label htmlFor="deepgram.smartFormat" className="inline-block">
                                {t('LiveTranscribe.Label.SmartFormat')}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </ContainerNoTopSpace>
    );
};
